import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { domains } from '../data/products';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Check, Sparkles, ChevronRight, Sprout, Milk, Flower2, Leaf, Waves,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const iconMap = { Sprout, Milk, Flower2, Leaf, Waves };

/* ─────────────────────────────────────────────────────────────
   Per-domain config
   • border  — left accent stripe + chevron + badge
   • fadeColor — the LEFT edge of the gradient fade (matches card bg)
   • images  — 3 unique relevant Unsplash photos
───────────────────────────────────────────────────────────── */
const DOMAIN_STYLE = {
  flowers: {
    border:    '#c2556e',
    fadeColor: '#fff0f3',
    badge:     'bg-rose-50 text-rose-700 border border-rose-200',
    images: [
      'https://images.unsplash.com/photo-1615280825886-fa817c0a06cc?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTh8fGZsb3dlcnN8ZW58MHx8MHx8fDA%3D', // pink roses annie spratt
      'https://images.unsplash.com/photo-1600647993560-11a92e039466?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fGZsb3dlcnN8ZW58MHx8MHx8fDA%3D', // colourful flower bunches
      'https://images.unsplash.com/photo-1519378058457-4c29a0a2efac?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGZsb3dlcnN8ZW58MHx8MHx8fDA%3D', // vibrant mixed arrangement
    ],
  },
  seafood: {
    border:    '#0a7fa0',
    fadeColor: '#eaf8ff',
    badge:     'bg-sky-50 text-sky-800 border border-sky-200',
    images: [
      'https://images.unsplash.com/photo-1565680018434-b513d5e5fd47?w=500&q=90', // raw shrimp on ice
      'https://images.unsplash.com/photo-1615834751896-b15e2330b289?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fGNyYWJ8ZW58MHx8MHx8fDA%3D', // whole crab / lobster
      'https://images.unsplash.com/photo-1559847844-5315695dadae?w=500&q=90', // seafood market display
    ],
  },
  'fruits-vegetables': {
    border:    '#1e8a3c',
    fadeColor: '#edfff3',
    badge:     'bg-emerald-50 text-emerald-800 border border-emerald-200',
    images: [
      'https://images.unsplash.com/photo-1610348725531-843dff563e2c?w=500&q=90', // colourful veggie market
      'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=500&q=90', // tropical fresh fruits
      'https://images.unsplash.com/photo-1597362925123-77861d3fbac7?w=500&q=90', // Indian market vegetables
    ],
  },
  fertilizers: {
    border:    '#7a5230',
    fadeColor: '#fdf6ec',
    badge:     'bg-amber-50 text-amber-800 border border-amber-200',
    images: [
      'https://plus.unsplash.com/premium_photo-1680125265832-ffaf364a8aca?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8ZmVydGlsaXplcnxlbnwwfHwwfHx8MA%3D%3D', // lush green farmland
      'https://plus.unsplash.com/premium_photo-1678652879155-bd82999aac9c?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTd8fGZlcnRpbGl6ZXJ8ZW58MHx8MHx8fDA%3D', // seedlings in rich soil
      'https://images.unsplash.com/photo-1655130944281-072e0644db75?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGZlcnRpbGl6ZXJ8ZW58MHx8MHx8fDA%3D', // golden wheat harvest
    ],
  },
  dairy: {
    border:    '#b8860b',
    fadeColor: '#fffbea',
    badge:     'bg-yellow-50 text-yellow-800 border border-yellow-200',
    images: [
      'https://media.istockphoto.com/id/825874520/photo/honey-among-toy-houses.webp?a=1&b=1&s=612x612&w=0&k=20&c=WZm9bLSwFARoliTDAthXbcOtPdj_ewJOwS9eOUy9beI=', // golden ghee jar
      'https://media.istockphoto.com/id/1413268613/photo/ghee-butter-oil.webp?a=1&b=1&s=612x612&w=0&k=20&c=yvVZ98yRgX0IOONQDtQnUuSbYf2bcwWLMXCqonlFjH8=', // dairy milk pour
      'https://media.istockphoto.com/id/1413268651/photo/ghee-butter-oil.webp?a=1&b=1&s=612x612&w=0&k=20&c=0LqtpOV7gzrXn7tOztQpPYF9cOgQYg1xdAYwNdO0u_I=', // cow in green field
    ],
  },
};

/* ─────────────────────────────────────────────────────────────
   CollageBackground
   KEY FIXES:
   • Removed the heavy tint overlay — images now show fully vivid
   • Only a narrow left-edge fade to blend into text area
   • saturate(1.3) brightness(1.05) for extra pop
   • Wave stripe for seafood in blue
───────────────────────────────────────────────────────────── */
const CollageBackground = ({ domainId }) => {
  const ds = DOMAIN_STYLE[domainId];
  if (!ds) return null;
  const [a, b, c] = ds.images;

  return (
    <div className="absolute inset-y-0 right-0 w-[52%] overflow-hidden pointer-events-none">

      {/* 3 vertical image strips — no gap for seamless look */}
      <div className="absolute inset-0 flex">
        <div className="relative flex-1 overflow-hidden">
          <img src={a} alt=""
               onError={e => { e.currentTarget.style.display = 'none'; }}
               className="absolute inset-0 w-full h-full object-cover"
               style={{ filter: 'saturate(1.35) brightness(1.06) contrast(1.04)' }} />
        </div>
        <div className="relative flex-1 overflow-hidden">
          <img src={b} alt=""
               onError={e => { e.currentTarget.style.display = 'none'; }}
               className="absolute inset-0 w-full h-full object-cover"
               style={{ filter: 'saturate(1.35) brightness(1.06) contrast(1.04)' }} />
        </div>
        <div className="relative flex-1 overflow-hidden hidden sm:block">
          <img src={c} alt=""
               onError={e => { e.currentTarget.style.display = 'none'; }}
               className="absolute inset-0 w-full h-full object-cover"
               style={{ filter: 'saturate(1.35) brightness(1.06) contrast(1.04)' }} />
        </div>
      </div>

      {/* thin divider lines between strips for a clean look */}
      <div className="absolute inset-0 flex pointer-events-none">
        <div className="flex-1" />
        <div className="w-px bg-white/40" />
        <div className="flex-1" />
        <div className="w-px bg-white/40 hidden sm:block" />
        <div className="flex-1 hidden sm:block" />
      </div>

      {/* LEFT fade — blends into white card background, ONLY 30% width, very short */}
      {/* <div className="absolute inset-y-0 left-0 w-[32%]"
           style={{
             background: `linear-gradient(to right, ${ds.fadeColor} 0%, ${ds.fadeColor}cc 50%, transparent 100%)`,
           }} /> */}

      {/* RIGHT edge soft vignette */}
      <div className="absolute inset-y-0 right-0 w-[12%]"
           style={{ background: 'linear-gradient(to left, rgba(0,0,0,0.12) 0%, transparent 100%)' }} />

      {/* TOP + BOTTOM vignette for blending with card border */}
      <div className="absolute inset-x-0 top-0 h-3"
           style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)' }} />
      <div className="absolute inset-x-0 bottom-0 h-3"
           style={{ background: 'linear-gradient(to top, rgba(255,255,255,0.5), transparent)' }} />

      {/* SEAFOOD ONLY: blue wave accent stripe at the bottom */}
      {domainId === 'seafood' && (
        <div className="absolute bottom-0 left-0 right-0 h-6 overflow-hidden">
          <svg viewBox="0 0 400 24" preserveAspectRatio="none"
               className="absolute inset-0 w-full h-full">
            <path d="M0,12 C50,0 100,24 150,12 C200,0 250,24 300,12 C350,0 400,20 400,12 L400,24 L0,24Z"
                  fill="#0a7fa0" opacity="0.55" />
            <path d="M0,16 C60,4 120,24 180,14 C240,4 300,22 360,14 C380,10 395,16 400,14 L400,24 L0,24Z"
                  fill="#0ea5c9" opacity="0.4" />
          </svg>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main page component
───────────────────────────────────────────────────────────── */
export default function ProductsPage() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeDomain,    setActiveDomain]    = useState(null);
  const navigate = useNavigate();

  const scrollToContact = () => {
    setSelectedProduct(null);
    setTimeout(() => {
      navigate('/');
      setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 150);
    }, 200);
  };

  return (
    <main data-testid="products-page" className="min-h-screen bg-cream">

      {/* ── Hero ── */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video autoPlay loop muted playsInline preload="auto"
            poster="https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80"
            data-testid="products-hero-video"
            className="absolute inset-0 w-full h-full object-cover">
            <source src="/shipping-hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-forest/80 via-forest/65 to-cream" />
        </div>
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.button initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            data-testid="products-back-button" onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white font-body text-sm mb-8 transition-colors group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </motion.button>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/20 text-white/90 px-4 py-1.5 rounded-full text-xs font-body tracking-widest uppercase mb-4">
              <Sparkles className="w-3 h-3" /> Our Product Range
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight">
              Product Categories
            </h1>
            <p className="text-base md:text-lg text-white/70 font-body mt-4 max-w-2xl tracking-wide">
              Explore our diverse range of premium products across five core categories.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Category list ── */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          {domains.map((domain, domainIndex) => {
            const IconComp = iconMap[domain.icon] || Sprout;
            const isActive = activeDomain === domain.id;
            const ds = DOMAIN_STYLE[domain.id] || {
              border: '#1a472a',
              fadeColor: '#ffffff',
              badge: 'bg-forest/10 text-forest border border-forest/20',
              images: [],
            };

            return (
              <motion.div key={domain.id} data-testid={`domain-section-${domain.id}`}
                initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: domainIndex * 0.07 }}
                className="mb-8 last:mb-0">

                {/* Category header card */}
                <button data-testid={`domain-toggle-${domain.id}`}
                  onClick={() => setActiveDomain(isActive ? null : domain.id)}
                  className="w-full group text-left">

                  <div
                    className="relative flex flex-col md:flex-row md:items-center
                                bg-white rounded-2xl border border-gray-100 overflow-hidden
                                hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-500"
                    style={{ borderLeft: `5px solid ${ds.border}`, minHeight: '128px' }}
                  >
                    {/* Vivid image collage — right side */}
                    <CollageBackground domainId={domain.id} />

                   
                

                    {/* Thumbnail */}
                    <div className="relative z-10 flex-shrink-0 w-full md:w-40 h-32 md:h-28
                                    overflow-hidden md:rounded-xl md:my-4 md:ml-5 md:mb-4">
                      <img src={domain.image} alt={domain.name}
                           className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                           style={{ filter: 'saturate(1.15)' }} />
                      <div className="absolute inset-0 flex items-center justify-center"
                           style={{ background: `${ds.border}77` }}>
                        <IconComp className="w-9 h-9 text-white drop-shadow-lg" />
                      </div>
                    </div>

                    {/* Text */}
                    <div className="relative z-10 flex-1 py-5 px-3 md:py-6 md:pl-1 md:pr-4">
                      <span className={`inline-block text-[11px] font-body font-semibold px-3 py-1 rounded-full mb-2.5 tracking-wide ${ds.badge}`}>
                        {domain.products.length} Products
                      </span>
                   <h2 className="text-gray-900 group-hover:text-forest transition-colors mb-2 leading-tight"
    style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(1.5rem, 2.6vw, 2.1rem)', fontWeight: 400 }}>
  {domain.name}
</h2>
                      <p className="text-[10.5px] font-body font-extrabold tracking-[0.24em] uppercase mb-2"
                         style={{ color: ds.border }}>
                        {domain.tagline}
                      </p>
                      <p className="text-[0.8rem] font-body text-gray-500 leading-relaxed tracking-wide line-clamp-2 max-w-sm">
                        {domain.description}
                      </p>
                    </div>

                    {/* Chevron */}
                    <div className="relative z-10 flex-shrink-0 hidden md:flex items-center pr-6">
                      <motion.div
                        animate={{ rotate: isActive ? 90 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="w-9 h-9 rounded-full border-2 flex items-center justify-center"
                        style={{
                          borderColor: ds.border,
                          background:  isActive ? ds.border : 'transparent',
                        }}>
                        <ChevronRight className="w-4 h-4" style={{ color: isActive ? '#fff' : ds.border }} />
                      </motion.div>
                    </div>
                  </div>
                </button>

                {/* Sub-cards */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.45, ease: 'easeInOut' }}
                      className="overflow-hidden">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4 px-1">
                        {domain.products.map((product, productIndex) => (
                          <motion.div key={product.id} data-testid={`product-card-${product.id}`}
                            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, delay: productIndex * 0.09 }}
                            whileHover={{ y: -7, transition: { duration: 0.25 } }}
                            onClick={() => setSelectedProduct({ ...product, domainName: domain.name })}
                            className="group/card cursor-pointer bg-white rounded-2xl overflow-hidden
                                       shadow-md hover:shadow-2xl border border-gray-100
                                       hover:border-forest/15 transition-all duration-300">
                            <div className="relative h-48 overflow-hidden">
                              <img src={product.image} alt={product.name}
                                   className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-700"
                                   style={{ filter: 'saturate(1.1)' }} />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent
                                              opacity-0 group-hover/card:opacity-100 transition-opacity duration-500" />
                              <div className="absolute bottom-4 left-4 opacity-0 group-hover/card:opacity-100
                                              translate-y-3 group-hover/card:translate-y-0 transition-all duration-400">
                                <span className="text-white text-[10px] font-body tracking-widest uppercase">
                                  Click for details
                                </span>
                              </div>
                              <div className="absolute top-0 left-0 right-0 h-[3px]"
                                   style={{ background: ds.border }} />
                            </div>
                            <div className="p-5">
                              <h3 className="font-heading font-bold text-gray-900 group-hover/card:text-forest
                                             transition-colors mb-2 leading-snug"
                                  style={{ fontSize: '1.05rem', letterSpacing: '-0.01em' }}>
                                {product.name}
                              </h3>
                              <p className="text-[0.8rem] font-body text-gray-500 leading-relaxed
                                            line-clamp-2 tracking-wide mb-4">
                                {product.description}
                              </p>
                              <button data-testid={`product-know-more-${product.id}`}
                                className="text-[0.72rem] font-body font-bold flex items-center gap-1
                                           group-hover/card:gap-2 transition-all tracking-[0.12em] uppercase"
                                style={{ color: ds.border }}>
                                Know More <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Detail dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        {selectedProduct && (
          <DialogContent data-testid="product-detail-dialog"
            className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl p-0 border-0 shadow-2xl">
            <div className="relative h-56 md:h-64 overflow-hidden rounded-t-2xl">
              <img src={selectedProduct.image} alt={selectedProduct.name}
                   className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <Badge className="bg-neon/90 text-forest border-0 text-xs font-body font-bold tracking-wide mb-2">
                  {selectedProduct.domainName}
                </Badge>
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl md:text-3xl font-bold text-white">
                    {selectedProduct.name}
                  </DialogTitle>
                  <DialogDescription className="text-white/70 text-sm font-body tracking-wide">
                    Premium quality for domestic &amp; export markets
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <p className="text-sm font-body text-gray-600 leading-relaxed mb-6 tracking-wide">
                {selectedProduct.description}
              </p>
              <Separator className="my-6" />
              <h4 className="font-heading text-base font-bold text-gray-900 mb-4">Product Highlights</h4>
              <ul className="space-y-3 mb-6">
                {selectedProduct.details.map((detail, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-forest" />
                    </div>
                    <span className="text-sm font-body text-gray-600 tracking-wide">{detail}</span>
                  </li>
                ))}
              </ul>
              <Separator className="my-6" />
              <h4 className="font-heading text-base font-bold text-gray-900 mb-3">Why Choose This Product?</h4>
              <p className="text-sm font-body text-gray-600 leading-relaxed tracking-wide">
                {selectedProduct.benefits}
              </p>
              <div className="mt-8 flex gap-3">
                <button data-testid="dialog-know-more-button" onClick={scrollToContact}
                  className="flex-1 bg-forest text-white font-body font-semibold py-3 rounded-full
                             hover:bg-forest-dark transition-all duration-300 text-sm tracking-wide">
                  Know More — Contact Us
                </button>
                <button data-testid="dialog-close-button" onClick={() => setSelectedProduct(null)}
                  className="px-6 py-3 border border-gray-200 text-gray-600 font-body font-medium
                             rounded-full hover:bg-gray-50 transition-all duration-300 text-sm">
                  Close
                </button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </main>
  );
}